import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import GridContainer from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridItem, Typography } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PackagingSlip from "@tKgpco/Components/OrderHistory/PackagingSlip";
import getPackagingSlipsHandler from "@tKgpco/Handlers/CustomPackagingSlipsHandler";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { FC, useCallback, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        isLoading: state.KGPState.customPackagingSlips.isLoading,
        packagingSlips: state.KGPState.customPackagingSlips.packagingSlips,
    };
};
const mapDispatchToProps = {
    getPackagingSlipsHandler,
};

export interface ProductSearchScoreDataStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    accordionSectionfalse?: AccordionPresentationProps;
    poLineTitleGridItem?: GridItemProps;
    packingSlipTitleGridItem?: GridItemProps;
    packingSlipCustomCSS?: InjectableCss;
    centeringWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
}

export const packagingSlipsStyles: ProductSearchScoreDataStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                > div {
                    border-bottom: 1px solid lightgrey;
                    padding-bottom: 15px;
                    margin-bottom: 15px;
                    &:last-of-type {
                        border: none;
                        margin-bottom: 0;
                    }
                }
            `,
        },
    },
    accordion: {
        css: css`
            @media print {
                button {
                    display: none !important;
                }
            }
        `,
    },

    accordionSectionfalse: {
        css: css`
            @media print {
                button {
                    display: -webkit-flex !important;
                    font-size: 14px !important;
                    align-items: center;
                }
                & div {
                    display: inline-flex !important;
                }
            }
        `,
    },
    poLineTitleGridItem: {
        width: 8,
        css: css`
            font-weight: 600;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
    packingSlipCustomCSS: {
        css: css`
            display: inline-flex;
        `,
    },
    packingSlipTitleGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            font-weight: 600;
            padding-right: 0;
        `,
    },

    centeringWrapper: {
        css: css`
            display: flex;
            height: 150px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const styles = packagingSlipsStyles;

const PackagingSlips: FC<Props> = ({ getPackagingSlipsHandler, isLoading, packagingSlips }) => {
    const { value: order } = useContext(OrderStateContext);
    const toasterContext = useContext(ToasterContext);
    const [handleAccordian, setHandleAccordian] = useState(false);

    const toggleAccordion = useCallback(
        (expanded: any) => {
            setHandleAccordian(!handleAccordian);
            if (order && order?.id && !isLoading && expanded) {
                setHandleAccordian(true);
                getPackagingSlipsHandler({
                    orderId: order.id,
                    onError: (error: any) => {
                        if (error) {
                            toasterContext.addToast({ body: translate(error.toString()), messageType: "danger" });
                        } else {
                            toasterContext.addToast({
                                body: translate("Something went wrong!"),
                                messageType: "danger",
                            });
                        }
                    },
                });
            }
        },
        [order?.id, packagingSlips],
    );

    return (
        <Accordion
            {...(handleAccordian ? { ...styles.accordionSectionfalse } : { ...styles.accordion })}
            headingLevel={3}
        >
            <ManagedAccordionSection
                title={
                    <GridContainer {...styles.packingSlipCustomCSS}>
                        <GridItem {...styles.packingSlipTitleGridItem}>{translate("Packing Slips")}</GridItem>
                        <GridItem {...styles.poLineTitleGridItem}>
                            {translate("PO Lines Shipped With Quantity")}
                        </GridItem>
                    </GridContainer>
                }
                initialExpanded={false}
                onTogglePanel={expanded => toggleAccordion(expanded)}
                {...styles.accordionSection}
            >
                {isLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.loadingSpinner} />
                    </StyledWrapper>
                ) : packagingSlips && packagingSlips.length > 0 ? (
                    packagingSlips?.map(item => {
                        return <PackagingSlip item={item} key={item.packagingSlipId} />;
                    })
                ) : (
                    packagingSlips &&
                    packagingSlips.length === 0 && (
                        <Typography as="p">No packaging slips found for this order.</Typography>
                    )
                )}
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PackagingSlips),
    definition: {
        displayName: "PackagingSlips",
        allowedContexts: ["OrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
